import {useMemo, useRef, useState} from 'react';
import {
  useEventWizardCoverPhoto,
  useEventWizardDoesPAYGUserHaveAITemplateLimit,
  useEventWizardEndDateTime,
  useEventWizardOnlineVenue,
  useEventWizardSelectedType,
  useEventWizardStartDateTime,
  useEventWizardTitle,
  useEventWizardVenue,
} from '@Components/event-wizard/event-wizard.hooks';
import {EventVenueType} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import {authenticateUser, getUserId, isUserPremium} from '@Libraries/user.library';
import {getDurationString} from '@Utils/date.util';
import type {EventsChangeCoverGA4Source} from '@Libraries/ga-events';
import {EventsChangeCoverGA4Type, GA4EventName, GA4EventParamName, trackGA4CustomEvent} from '@Libraries/ga-events';
import {
  onRemoveMedia,
  openDesignSelectorEvents,
  openEmbeddedEditor,
  openGalleryTemplateSelector,
  openUploadSelector,
  shouldUpsellAITemplateTagging,
} from './event-wizard-image-video.helper';
import type {EventTypeAiSearchParams, EventWizardImageVideoSources} from './event-wizard-image-video.types';
import {MEDIA_TYPE_COVER_GA4_TYPES, MediaTypes} from './event-wizard-image-video.types';
import {useCoverPhotoPickerLoading} from './use-cover-photo-picker-loading';

/**
 * The cover photo's sources (AI designs, My designs, Uploads), its design editor and Remove, shared by every
 * EventWizardImageVideo variant so step 1's tile and step 3's section open exactly the same flows. Each one reports
 * loading from the click until its picker is on screen, and reports the change to GA4 with `ga4Source`, the control
 * it was made from.
 */
export function useEventWizardImageVideoSources(ga4Source: EventsChangeCoverGA4Source): EventWizardImageVideoSources {
  const coverPhoto = useEventWizardCoverPhoto();
  const [uploadedMediaType, setUploadedMediaType] = useState<MediaTypes | undefined>(coverPhoto.mediaType);
  const {loadingMediaType, startLoading, stopLoading} = useCoverPhotoPickerLoading();
  const eventName = useEventWizardTitle();
  const selectedStartDateTime = useEventWizardStartDateTime();
  const selectedEndDateTime = useEventWizardEndDateTime();
  const eventTimings = `${selectedStartDateTime.toTimeString()} to ${selectedEndDateTime.toTimeString()}`;
  const eventDuration = useMemo((): string => {
    return getDurationString(selectedStartDateTime, selectedEndDateTime);
  }, [selectedStartDateTime, selectedEndDateTime]);
  const {selectedVenueType, offlineVenue} = useEventWizardVenue();
  const onlineEventURL = useEventWizardOnlineVenue() ?? '';
  const eventLocation = selectedVenueType === EventVenueType.OFFLINE ? (offlineVenue?.name ?? '') : onlineEventURL;
  const eventDates = `${selectedStartDateTime.toDateString()} to ${selectedEndDateTime.toDateString()}`;
  const selectedEventType = useEventWizardSelectedType();
  const eventType = selectedEventType?.name ?? '';
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());
  const doesPAYGUserHaveAITemplateLimit = useEventWizardDoesPAYGUserHaveAITemplateLimit();

  const trackCoverChange = (ga4Type: EventsChangeCoverGA4Type): void => {
    trackGA4CustomEvent(GA4EventName.EVENT_WIZARD_CHANGE_COVER, {
      [GA4EventParamName.SOURCE]: ga4Source,
      [GA4EventParamName.TYPE]: ga4Type,
    });
  };

  const onAuthenticatedUserMediaSelectionFlow = (mediaType: MediaTypes): void => {
    if (mediaType === MediaTypes.MY_DESIGNS) {
      openDesignSelectorEvents(stopLoading);
    } else if (mediaType === MediaTypes.TEMPLATES) {
      const aiSearchParams: EventTypeAiSearchParams | undefined = selectedEventType !== null ? {eventName, eventTypeHashedId: selectedEventType.hashedID} : undefined;
      openGalleryTemplateSelector(
        stopLoading,
        aiSearchParams,
        eventName.length > 0 ? {eventDates, eventName, eventDuration, eventTimings, eventLocation, eventType} : undefined,
        shouldUpsellAITemplateTagging(doesPAYGUserHaveAITemplateLimit)
      );
    }
  };

  const openAuthenticatedUserMediaSelectionFlow = (mediaType: MediaTypes): void => {
    void authenticateUser(() => {
      onAuthenticatedUserMediaSelectionFlow(mediaType);
    }, stopLoading, {
      panelText: window.i18next.t('pmwjs_make_account_to_share_event'),
      panelSubtext: window.i18next.t('pmwjs_login_event_promotion_subtext'),
    });
  };

  const openMediaSourceIfSet = (mediaType: MediaTypes | undefined): void => {
    setUploadedMediaType(mediaType);

    if (mediaType === undefined) {
      return;
    }
    trackCoverChange(MEDIA_TYPE_COVER_GA4_TYPES[mediaType]);
    startLoading(mediaType);

    if (mediaType === MediaTypes.UPLOAD) {
      openUploadSelector();
      return;
    }
    openAuthenticatedUserMediaSelectionFlow(mediaType);
  };

  const openCoverDesignEditor = (): void => {
    if (uploadedMediaType !== undefined && !!coverPhoto.selectedTemplateHID) {
      trackCoverChange(EventsChangeCoverGA4Type.EDIT_DESIGN);
      startLoading(uploadedMediaType);
      openEmbeddedEditor({hid: coverPhoto.selectedTemplateHID, uploadedMediaType, shouldUpsellAIPrefill: isPremium.current, onOpened: stopLoading});
    }
  };

  return {
    loadingMediaType,
    canEditCoverDesign: coverPhoto.selectedTemplateHID !== undefined && coverPhoto.mediaType !== MediaTypes.UPLOAD,
    openMediaSource: openMediaSourceIfSet,
    replaceCoverPhoto: (): void => {
      openMediaSourceIfSet(uploadedMediaType);
    },
    openCoverDesignEditor,
    removeCoverPhoto: (): void => {
      trackCoverChange(EventsChangeCoverGA4Type.REMOVE);
      onRemoveMedia();
    },
  };
}
